import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { SearchProject } from "../../http/types/projects";
import { useConfigurationsApi } from "../../http/useConfigurations";
import { useProjectsApi } from "../../http/useProjects";
import SvgAdd from "../icons/Add";
import CreateProjectForm from "../projects/CreateProjectForm";
import Button from "../ui/Button";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import ModalDialog from "../ui/ModalDialog";
import { CustomSelect } from "../ui/select/CustomSelect";
import styles from "./AddConfigurationToProjectDialog.module.css";

interface AddConfigurationToProjectDialogForm {
  project: SearchProject | null;
}

interface AddConfigurationToProjectDialogProps {
  configurationId: string;
  onClose: () => void;
  onSave: () => void;
}

const AddConfigurationToProjectDialog: FC<AddConfigurationToProjectDialogProps> = ({
  configurationId,
  onClose,
  onSave,
}: AddConfigurationToProjectDialogProps) => {
  const i18n = useI18n();
  const projectsApi = useProjectsApi();
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const configurationsApi = useConfigurationsApi();

  const [page, setPage] = useState<"select" | "project">("select");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newProjectId, setNewProjectId] = useState<string>();

  const { data, trigger } = projectsApi.getMyProjects(aborter.current.signal);

  useEffect(() => {
    trigger();
  }, []);

  const editableProjects = data?.filter((x) => x.canEdit);

  return (
    <ModalDialog
      onClose={() => (page == "select" ? onClose() : setPage("select"))}
      title={page == "select" ? i18n.translation.configurations.addToProject : i18n.translation.projects.createNew}
    >
      {page == "select" && (
        <Form<AddConfigurationToProjectDialogForm>
          disabled={isSubmitting}
          onSubmit={() => async (formData) => {
            setIsSubmitting(true);
            return configurationsApi
              .addConfigurationToProject(configurationId, formData.project!.id)
              .then(() => onSave())
              .catch((err) => dispatch(showErrorNotification(i18n, err)))
              .finally(() => setIsSubmitting(false));
          }}
          submitText={i18n.translation.common.ok}
          secondaryButton={
            <Button buttonProps={{ disabled: isSubmitting, onClick: () => onClose() }} type="secondary">
              {i18n.translation.common.cancel}
            </Button>
          }
        >
          {({ control, setValue }) => {
            useEffect(() => {
              if (newProjectId) {
                trigger().then((items) => {
                  setValue("project", items?.find((x) => x.id === newProjectId) ?? null);
                });
              }
            }, [newProjectId]);

            return (
              <FormFieldsContainer fullWidth>
                <Controller
                  name="project"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.translation.common.selectProject} error={fieldState.error}>
                      {({ labelId }) => (
                        <div className={styles.selectWithButton}>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={editableProjects}
                            isDisabled={isSubmitting}
                            getOptionValue={(x) => x.id}
                            getOptionLabel={(x) => x.name}
                          />
                          <Button
                            buttonProps={{
                              title: i18n.translation.projects.createNew,
                              className: styles.button,
                              disabled: isSubmitting,
                              onClick: () => setPage("project"),
                            }}
                            glyph={SvgAdd}
                          />
                        </div>
                      )}
                    </FormField>
                  )}
                />
              </FormFieldsContainer>
            );
          }}
        </Form>
      )}
      {page == "project" && (
        <CreateProjectForm
          onSubmit={(projectId: string) => {
            setIsSubmitting(false);
            setNewProjectId(projectId);
            setPage("select");
          }}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
          closeModal={() => setPage("select")}
        />
      )}
    </ModalDialog>
  );
};

export default AddConfigurationToProjectDialog;
